import useInterval from "@/hooks/useInterval";
import { updateUserData } from "@/services/auth.service";
import { confirmVerifyPhoneCode, requestVerifyPhoneCode } from "@/services/open-mall.service";
import {
    Box, Button, CardMedia, Collapse, InputAdornment,
    TextField, Typography
} from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import MobileIconImg from '@/assets/images/sources/mobile.png'
import { LoadingButton } from "@/components/common/LoadingButton";

export default function AddPhoneNumber({ setStep }: {
    setStep: React.Dispatch<React.SetStateAction<number>>
}) {
    const { path = '', is_mall_opened = false } = useLoaderData() as {
        path: string; email: string; phone_number: string; is_mall_opened: boolean
    }
    const navigate = useNavigate()
    const submitBtnRef = useRef<HTMLButtonElement>(null)
    const { userId = '' } = useParams()
    const [phoneValue, setPhoneValue] = useState('')
    const [validTime, setValidTime] = useState(0)
    const [authCode, setAuthCode] = useState('')
    const [isValid, setIsValid] = useState(false)
    const [loading, setLoading] = useState(false)
    const displayLimitTime = useMemo(() => {
        const limitSec = validTime;
        const h = Math.floor(limitSec / (60 * 60));
        const m = Math.floor((limitSec / 60) % 60);
        const s = limitSec % 60;
        const hh = h < 0 ? `${`${h}`.padStart(2, '0')}:` : '';
        const mm = `${m}`.padStart(2, '0');
        const ss = `${s}`.padStart(2, '0');

        return `${hh}${mm}:${ss}`;
    }, [validTime])

    useInterval(() => {
        if (!!isValid) return;
        if (validTime === 0) {
            return;
        }
        setValidTime(() => {
            if ((validTime - 1) === 0) {
                setAuthCode('')
                setIsValid(false)
            }
            return validTime - 1
        });
    }, 1000)
    return (
        <Box className="flex flex-col" sx={{ gap: 20 }}>
            <Box
                className="flex w-full flex-col justify-center items-center"
                sx={{
                    width: '100%',
                }}
            >
                <Box className="flex justify-center items-center" sx={{ mb: 2 }}>
                    <CardMedia
                        sx={{ width: '30px', mr: .5 }}
                        component={'img'}
                        src={MobileIconImg}
                    />
                    <Typography variant="h6" color={'primary'}>휴대폰번호</Typography>
                    <Typography variant="h6">를 입력해주세요.</Typography>
                </Box>
                <TextField fullWidth
                    value={phoneValue}
                    name="phoneNumber"
                    onChange={(e) => {
                        if (isNaN(Number(e.target.value))) {
                            toast.warning('숫자만 입력해주세요.', { toastId: 'is not number' })
                            return;
                        }
                        setIsValid(false)
                        setValidTime(0)
                        setAuthCode('')
                        setPhoneValue(e.target.value)
                    }}
                    helperText="휴대폰번호를 적어주세요 ( - 제외)"
                    inputProps={{
                        maxLength: '20', inputMode: 'numeric', pattern: '[0-9]*'
                    }}
                    type="text"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <LoadingButton isLoading={loading} variant="contained"
                                    disabled={(validTime > 0) || !!isValid}
                                    onClick={async () => {
                                        try {
                                            if (!phoneValue.trim()) {
                                                toast.warning('휴대폰번호를 입력해주세요.', { toastId: 'empty' })
                                                return;
                                            }
                                            const checkReg = /^01([0|1|6|7|8|9])-?([0-9]{3,4})-?([0-9]{4})$/.test(phoneValue)
                                            if (!checkReg) {
                                                toast.warning('올바른 휴대폰번호 형식이 아닙니다.', { toastId: 'not passed' })
                                                return;
                                            }
                                            setLoading(true)
                                            await requestVerifyPhoneCode(phoneValue)
                                            setValidTime(60)
                                            toast.info('입력하신 번호로 인증코드가 전송되었습니다.')
                                        } catch (error) {
                                            toast.error('인증코드를 발송하지 못했습니다. 잠시 후 다시 시도해주세요.', { toastId: 'error catch' })
                                            throw error
                                        } finally {
                                            setLoading(false)
                                        }
                                    }}
                                >인증코드 발송</LoadingButton>
                            </InputAdornment>
                        ),
                        style: {
                            backgroundColor: '#ffffff',
                            borderRadius: '6px'
                        },
                    }}
                />
                <Collapse in={validTime > 0} className="w-full">
                    <TextField fullWidth
                        name="authCode"
                        helperText="휴대폰으로 발송된 인증코드 6자리를 입력해주세요."
                        value={authCode}
                        onChange={(e) => {
                            if (isNaN(Number(e.target.value))) {
                                toast.warning('숫자만 입력해주세요.', { toastId: 'is not number' })
                                return;
                            }
                            setAuthCode(e.target.value)
                        }}
                        inputProps={{
                            maxLength: '6', inputMode: 'numeric', pattern: '[0-9]*'
                        }}
                        onKeyDown={(event) => {
                            const { altKey, ctrlKey, key } = event;
                            if (altKey || ctrlKey) return;
                            if (key === 'Enter') {
                                console.log('is enter')
                                if (!!submitBtnRef.current) {
                                    console.log('is ref')
                                    submitBtnRef.current.click()
                                }
                            }
                        }}
                        type="text"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography>{displayLimitTime}</Typography>
                                </InputAdornment>
                            ),
                            style: {
                                backgroundColor: '#ffffff',
                                borderRadius: '6px'
                            },
                        }}
                    />
                </Collapse>
            </Box>
            <Button ref={submitBtnRef} fullWidth
                variant="contained" size="large"
                disabled={!authCode || validTime === 0}
                onClick={async () => {
                    setLoading(true)
                    try {
                        await confirmVerifyPhoneCode(phoneValue, authCode).catch((e: any) => {
                            throw {
                                place_holder: e?.data?.place_holder || '인증에 실패했습니다. 다시 한번 시도해주세요.',
                                type: 'auth'
                            }
                        })
                        setValidTime(0)
                        setIsValid(true)
                        await updateUserData(userId, { phone_number: phoneValue }).catch((e: any) => {
                            throw {
                                place_holder: e?.data?.place_holder || '사용자 정보 업데이트에 실패했습니다. 잠시 후 다시 시도해주세요.',
                                type: 'update'
                            }
                        })
                        toast.success('사용자 정보가 업데이트되었습니다.')
                        if (!!is_mall_opened) {
                            navigate('/')
                        } else {
                            setStep(1)
                        }
                    } catch (error: any) {
                        console.log('error::::', error)
                        const message = error?.place_holder || '인증에 실패했습니다. 다시 한번 시도해주세요.'
                        toast.error(message)
                        setIsValid(false)
                        setValidTime(0)
                        setAuthCode('')
                        throw error
                    } finally {
                        setLoading(false)
                    }
                }}
            >
                <b>{!!path ? '시작하기' : '인증완료'}</b>
            </Button>
        </Box>
    )
}